import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ensureCurrentUser } from '../../util/data';
import { NamedLink } from '../../components';
import css from './ModalCompleteProfile.css';
import { updateProfileLive } from '../../ducks/user.duck';
import { FormattedMessage } from '../../util/reactIntl';
import {
  USER_ROLES,
  computeProfileData,
  computeProfilePoints,
  computePortfolioPoints,
  isProfileLiveEligible,
  getScoreLabelId,
  CircularScore,
} from './profilePoints';

import { MdOutlineDescription, MdLanguage, MdCheckCircle } from 'react-icons/md';
import { BiBriefcase, BiSolidQuoteAltRight, BiTargetLock } from 'react-icons/bi';
import { FaRegUserCircle } from 'react-icons/fa';
import { HiOutlineThumbUp } from 'react-icons/hi';
import { HiOutlineDocumentCheck, HiOutlineWrench } from 'react-icons/hi2';
import { IoIosArrowForward, IoMdInformationCircleOutline } from 'react-icons/io';
import { LuBuilding, LuLayout, LuText } from 'react-icons/lu';
import { TbCoinEuro, TbSchool } from 'react-icons/tb';
import { BsCheckCircleFill } from 'react-icons/bs';

export { USER_ROLES };

// ================ Tile definitions ================ //

const PRE_LIVE_TILES = [
  {
    key: 'workExperience',
    icon: LuBuilding,
    titleId: 'DashboardPage.tileWorkExperienceTitle',
    descriptionId: 'DashboardPage.tileWorkExperienceDescription',
    hash: '#workExperience',
    priority: true,
  },
  {
    key: 'workedWith',
    icon: BiBriefcase,
    titleId: 'DashboardPage.tileWorkedWithTitle',
    descriptionId: 'DashboardPage.tileWorkedWithDescription',
    hash: '#workedWith',
    priority: true,
  },
  {
    key: 'bio',
    icon: LuText,
    titleId: 'DashboardPage.tileBioTitle',
    descriptionId: 'DashboardPage.tileBioDescription',
    hash: '#bio',
  },
  {
    key: 'experience',
    icon: BiTargetLock,
    titleId: 'DashboardPage.tileExperienceTitle',
    descriptionId: 'DashboardPage.tileExperienceDescription',
    hash: '#experience',
  },
  {
    key: 'tools',
    icon: HiOutlineWrench,
    titleId: 'DashboardPage.tileToolsTitle',
    descriptionId: 'DashboardPage.tileToolsDescription',
    hash: '#tools',
  },
];

const ALWAYS_SHOWN_TILES = [
  {
    key: 'portfolio',
    icon: LuLayout,
    titleId: 'DashboardPage.tilePortfolioTitle',
    descriptionId: 'DashboardPage.tilePortfolioDescription',
    hash: '#portfolio',
    pointsPerItem: true,
  },
  {
    key: 'testimonials',
    icon: BiSolidQuoteAltRight,
    titleId: 'DashboardPage.tileTestimonialsTitle',
    descriptionId: 'DashboardPage.tileTestimonialsDescription',
    hash: '#testimonials',
    pointsPerItem: true,
  },
];

const POST_LIVE_OPTIONAL_TILES = [
  {
    key: 'hobbies',
    icon: HiOutlineThumbUp,
    titleId: 'DashboardPage.tileHobbiesTitle',
    descriptionId: 'DashboardPage.tileHobbiesDescription',
    hash: '#hobbies',
    points: 5,
  },
  {
    key: 'education',
    icon: TbSchool,
    titleId: 'DashboardPage.tileEducationTitle',
    descriptionId: 'DashboardPage.tileEducationDescription',
    hash: '#education',
    points: 5,
  },
  {
    key: 'hourlyFee',
    icon: TbCoinEuro,
    titleId: 'DashboardPage.tileHourlyFeeTitle',
    descriptionId: 'DashboardPage.tileHourlyFeeDescription',
    hash: '#hourlyFee',
    points: 5,
  },
  {
    key: 'cocAccepted',
    icon: HiOutlineDocumentCheck,
    titleId: 'DashboardPage.tileCocTitle',
    descriptionId: 'DashboardPage.tileCocDescription',
    hash: '#cocAccepted',
    points: 5,
  },
  {
    key: 'profileImage',
    icon: FaRegUserCircle,
    titleId: 'DashboardPage.tileProfileImageTitle',
    descriptionId: 'DashboardPage.tileProfileImageDescription',
    hash: '#profileImage',
    points: 10,
  },
  {
    key: 'whoAmI',
    icon: MdOutlineDescription,
    titleId: 'DashboardPage.tileWhoAmITitle',
    descriptionId: 'DashboardPage.tileWhoAmIDescription',
    hash: '#whoAmI',
    points: 10,
  },
  {
    key: 'languages',
    icon: MdLanguage,
    titleId: 'DashboardPage.tileLanguagesTitle',
    descriptionId: 'DashboardPage.tileLanguagesDescription',
    hash: '#languages',
    points: 5,
  },
  // Show WE/PC in post-live too if still unfilled
  {
    key: 'workExperience',
    icon: LuBuilding,
    titleId: 'DashboardPage.tileWorkExperienceTitle',
    descriptionId: 'DashboardPage.tileWorkExperienceDescription',
    hash: '#workExperience',
    points: 10,
  },
  {
    key: 'workedWith',
    icon: BiBriefcase,
    titleId: 'DashboardPage.tileWorkedWithTitle',
    descriptionId: 'DashboardPage.tileWorkedWithDescription',
    hash: '#workedWith',
    points: 10,
  },
];

// ================ Tile rendering ================ //

const TileCard = ({ tile, badgeContent }) => {
  const Icon = tile.icon;
  return (
    <NamedLink name="ProfileSettingsPage" to={{ hash: tile.hash }} className={css.tileCard}>
      <div className={css.tileHeader}>
        <Icon className={css.tileIcon} />
        <div className={css.tileTitle}>
          <FormattedMessage id={tile.titleId} />
        </div>
      </div>
      <div className={css.tileDescription}>
        <FormattedMessage id={tile.descriptionId} />
      </div>
      <div className={css.tileBadge}>
        <IoMdInformationCircleOutline className={css.tileBadgeIcon} />
        {badgeContent}
      </div>
    </NamedLink>
  );
};

const RequiredBadge = () => (
  <span className={css.tileBadgeText}>
    <FormattedMessage id="DashboardPage.tileBadgeRequired" />
  </span>
);

const PointsBadge = ({ points }) => (
  <span className={css.tileBadgeText}>
    <FormattedMessage id="DashboardPage.tileBadgePoints" values={{ points }} />
  </span>
);

const PointsPerBadge = () => (
  <span className={css.tileBadgeText}>
    <FormattedMessage id="DashboardPage.tileBadgePointsPer" />
    <span className={css.tileBadgeTextMuted}>
      <FormattedMessage id="DashboardPage.tileBadgePointsPerMax" />
    </span>
  </span>
);

// Private work-experience/client entries don't count towards activation, so a
// user who has added only private ones sees the section as "filled" — swap the
// tile description for one that explains public entries are needed.
const withPrivateOnlyHint = (tile, profileData) => {
  if (tile.key === 'workExperience' && profileData.privateWorkExperienceOnly) {
    return { ...tile, descriptionId: 'DashboardPage.tileWorkExperiencePrivateOnlyDescription' };
  }
  if (tile.key === 'workedWith' && profileData.privateWorkedWithOnly) {
    return { ...tile, descriptionId: 'DashboardPage.tileWorkedWithPrivateOnlyDescription' };
  }
  return tile;
};

// Pre-live tiles set tile.badgeKind explicitly. Post-live tiles fall back to
// pointsPerItem / points from the source array.
const renderBadge = tile => {
  if (tile.badgeKind === 'required') return <RequiredBadge />;
  if (tile.badgeKind === 'points') return <PointsBadge points={tile.points} />;
  if (tile.pointsPerItem) return <PointsPerBadge />;
  return <PointsBadge points={tile.points} />;
};

// ================ Main Component ================ //

// showKeepUpdatedTile / compactWhenComplete let ProfileSettingsPage drop the
// "keep your profile updated" tile and swap the full card for a compact
// points banner once every profile field except the per-item portfolio and
// testimonial tiles is filled. The defaults keep the dashboard behavior.
function ModalCompleteProfile({ showKeepUpdatedTile = true, compactWhenComplete = false }) {
  const dispatch = useDispatch();
  const updateInProgressRef = useRef(false);

  const { user, currentUserHasPortfolioWorkListings, currentUserHasTestimonials } = useSelector(
    store => {
      const {
        currentUser,
        currentUserHasPortfolioWorkListings,
        currentUserHasTestimonials,
      } = store.user;
      return {
        user: currentUser,
        currentUserHasPortfolioWorkListings,
        currentUserHasTestimonials,
      };
    }
  );

  const currentUser = ensureCurrentUser(user);
  const accountRole = currentUser?.attributes?.profile?.publicData?.account_role;
  const isCustomer = accountRole === USER_ROLES.CUSTOMER;

  const profileData = useMemo(() => computeProfileData(currentUser), [currentUser]);
  const profileOnlyPoints = useMemo(() => computeProfilePoints(profileData), [profileData]);
  const portfolioPoints = useMemo(() => computePortfolioPoints(currentUser), [currentUser]);
  const totalPoints = profileOnlyPoints + portfolioPoints;
  const shouldBeLive = useMemo(() => isProfileLiveEligible(profileData, profileOnlyPoints), [
    profileData,
    profileOnlyPoints,
  ]);

  const currentMetadataLive = !!currentUser?.attributes?.profile?.metadata?.profileLive;

  // Sync metadata.profileLive when eligibility changes
  useEffect(() => {
    if (isCustomer) return;
    if (shouldBeLive !== currentMetadataLive && !updateInProgressRef.current) {
      updateInProgressRef.current = true;
      dispatch(updateProfileLive(shouldBeLive)).finally(() => {
        updateInProgressRef.current = false;
      });
    }
  }, [shouldBeLive, currentMetadataLive, dispatch, isCustomer]);

  const isLive = shouldBeLive;
  const scoreLabelId = getScoreLabelId(totalPoints, isLive);

  // Determine which tiles to show
  const { topRowTiles, remainingTiles, allBaseFilled, remainingGridClass } = useMemo(() => {
    // workExperience and workedWith form an OR pair — one public entry is
    // enough, so once either is filled neither tile is shown anywhere.
    // Showing the other as "unfilled" reads as a contradiction next to an
    // active profile.
    const hasWorkHistory = profileData.workExperience || profileData.workedWith;
    const withoutFilledWorkPair = tiles =>
      tiles.filter(
        t => !(hasWorkHistory && (t.key === 'workExperience' || t.key === 'workedWith'))
      );

    if (!isLive) {
      const allRequiredMet =
        profileData.bio && profileData.experience && profileData.tools && hasWorkHistory;

      // All hard gates satisfied but still under the points threshold —
      // surface the full optional set so the user can pick how to reach it.
      if (allRequiredMet) {
        const unfilledOptional = withoutFilledWorkPair(
          POST_LIVE_OPTIONAL_TILES.filter(t => !profileData[t.key])
        );
        return {
          topRowTiles: ALWAYS_SHOWN_TILES,
          remainingTiles: unfilledOptional,
          allBaseFilled: false,
          remainingGridClass: 'gridCols2',
        };
      }

      // Pre-live: show only unfilled required tiles. The work pair only
      // reaches this point when both halves are missing, so every tile
      // shown here is a hard requirement.
      const unfilled = withoutFilledWorkPair(
        PRE_LIVE_TILES.filter(t => !profileData[t.key])
      ).map(t => ({ ...t, badgeKind: 'required' }));
      const count = unfilled.length;

      // 4 items → 2x2 grid; <4 items → all on one row; 5 items → 2 priority on top + 3 below
      if (count <= 4) {
        const gridClass =
          count === 4
            ? 'gridCols2'
            : count === 3
            ? 'bottomRow3'
            : count === 2
            ? 'gridCols2'
            : 'gridCols1';
        return {
          topRowTiles: [],
          remainingTiles: unfilled,
          allBaseFilled: false,
          remainingGridClass: gridClass,
        };
      }

      const top = unfilled.filter(t => t.priority);
      const rest = unfilled.filter(t => !t.priority);
      return {
        topRowTiles: top,
        remainingTiles: rest,
        allBaseFilled: false,
        remainingGridClass: 'bottomRow3',
      };
    }

    // Post-live: portfolio & testimonials always at top
    const topRow = ALWAYS_SHOWN_TILES;

    // Filter optional tiles to only unfilled ones. A live profile always has
    // work history, so the work pair never shows here.
    const unfilledOptional = withoutFilledWorkPair(
      POST_LIVE_OPTIONAL_TILES.filter(t => !profileData[t.key])
    );

    const allFilled = unfilledOptional.length === 0;

    return {
      topRowTiles: topRow,
      remainingTiles: unfilledOptional,
      allBaseFilled: allFilled,
      remainingGridClass: 'gridCols2',
    };
  }, [isLive, profileData]);

  const hasTiles = topRowTiles.length > 0 || remainingTiles.length > 0;

  // Only render for freelancers/light-entrepreneurs
  if (isCustomer) return null;

  // Everything except the per-item portfolio and testimonial tiles is filled:
  // show only the compact points banner instead of the full tiles card.
  if (compactWhenComplete && isLive && allBaseFilled) {
    return (
      <div className={css.progressBanner}>
        <CircularScore points={totalPoints} />
        <div className={css.progressBannerTextWrap}>
          <div className={css.progressBannerLabel}>
            <FormattedMessage id="DashboardPage.profilePointsLabel" />
          </div>
          <div className={css.progressBannerSubtextRow}>
            <span className={css.progressBannerSubtextLive}>
              <FormattedMessage id="DashboardPage.profileLiveSubtext" />
            </span>
          </div>
        </div>
        <div className={css.progressBannerActivePill}>
          <MdCheckCircle className={css.progressBannerActivePillIcon} />
          <span className={css.progressBannerActivePillText}>
            <FormattedMessage id="DashboardPage.profileActivePill" />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={css.completeCustomerContainer}>
      <div className={css.headerRow}>
        <div className={css.header}>
          <FormattedMessage
            id={isLive ? 'DashboardPage.profileLiveTitle' : 'DashboardPage.profileNotLiveTitle'}
          />
        </div>
        {isLive && (
          <span className={css.activePill}>
            <BsCheckCircleFill className={css.activePillIcon} />
            <FormattedMessage id="DashboardPage.profileActivePill" />
          </span>
        )}
      </div>

      <div className={css.scoreSection}>
        <CircularScore points={totalPoints} />
        <div className={css.scoreTextWrap}>
          <div className={css.scoreLabel}>
            <FormattedMessage id={isLive ? 'DashboardPage.profilePointsLabel' : scoreLabelId} />
          </div>
          <div className={css.scoreSubtextRow}>
            <IoMdInformationCircleOutline className={css.scoreInfoIcon} />
            <span className={css.scoreSubtext}>
              <FormattedMessage
                id={
                  isLive
                    ? 'DashboardPage.profileLiveSubtext'
                    : 'DashboardPage.profileNotLiveSubtext'
                }
              />
            </span>
          </div>
        </div>
      </div>

      {hasTiles && (
        <div className={css.tilesContainer}>
          {topRowTiles.length > 0 && (
            <div className={css.topRow}>
              {topRowTiles.map(tile => {
                const t = withPrivateOnlyHint(tile, profileData);
                return <TileCard key={t.key} tile={t} badgeContent={renderBadge(t)} />;
              })}
            </div>
          )}

          {remainingTiles.length > 0 && (
            <div className={css[remainingGridClass]}>
              {remainingTiles.map(tile => {
                const t = withPrivateOnlyHint(tile, profileData);
                return <TileCard key={t.key} tile={t} badgeContent={renderBadge(t)} />;
              })}
            </div>
          )}
        </div>
      )}

      {isLive && allBaseFilled && showKeepUpdatedTile && (
        <NamedLink name="ProfileSettingsPage" className={css.keepUpdatedTile}>
          <div className={css.keepUpdatedContent}>
            <FaRegUserCircle className={css.keepUpdatedIcon} />
            <div className={css.keepUpdatedTextWrapper}>
              <div className={css.keepUpdatedTitle}>
                <FormattedMessage id="DashboardPage.keepProfileUpdatedTitle" />
              </div>
            </div>
          </div>
          <IoIosArrowForward className={css.keepUpdatedArrow} />
        </NamedLink>
      )}
    </div>
  );
}

export default React.memo(ModalCompleteProfile);
